import type { CancelablePromise } from "../core/CancelablePromise";
import { OpenAPIHousien } from "../core/OpenAPI";
import { request as __request } from "../core/request";

/** Structured free-text note on a property (Overview). */
export interface PropertyNote {
  note_id: string;
  content: string;
  created_by: string;
  created_at: string;
  updated_at?: string | null;
}

/**
 * Lists all free-text notes on a property via
 * `GET /agencies/{agencyId}/properties/{propertyId}/notes`.
 *
 * @param agencyId - The agency identifier
 * @param propertyId - The property identifier
 * @returns A cancelable promise resolving to the property's notes
 */
export function getPropertyNotes(
  agencyId: string,
  propertyId: string,
): CancelablePromise<PropertyNote[]> {
  return __request(OpenAPIHousien, {
    method: "GET",
    url: "/agencies/{agencyId}/properties/{propertyId}/notes",
    path: { agencyId, propertyId },
  });
}

/**
 * Creates a free-text note on a property via
 * `POST /agencies/{agencyId}/properties/{propertyId}/notes`.
 *
 * @param agencyId - The agency identifier
 * @param propertyId - The property identifier
 * @param content - The note body text
 * @returns A cancelable promise resolving to the created note
 */
export function createPropertyNote(
  agencyId: string,
  propertyId: string,
  content: string,
): CancelablePromise<PropertyNote> {
  return __request(OpenAPIHousien, {
    method: "POST",
    url: "/agencies/{agencyId}/properties/{propertyId}/notes",
    path: { agencyId, propertyId },
    body: { content },
    mediaType: "application/json",
  });
}

/**
 * Deletes a property note via
 * `DELETE /agencies/{agencyId}/properties/{propertyId}/notes/{noteId}`.
 *
 * @param agencyId - The owning agency
 * @param propertyId - The property the note belongs to
 * @param noteId - The note to delete
 * @returns A cancelable promise resolving to the deletion message payload
 */
export function deletePropertyNote(
  agencyId: string,
  propertyId: string,
  noteId: string,
): CancelablePromise<{ message?: string }> {
  return __request(OpenAPIHousien, {
    method: "DELETE",
    url: "/agencies/{agencyId}/properties/{propertyId}/notes/{noteId}",
    path: { agencyId, propertyId, noteId },
  });
}
